import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import {
  Stack,
  Typography,
  Container,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  CircularProgress, // Importe CircularProgress
  Alert,
} from '@mui/material';

import { EntityContext } from '../entity';
import { createWebPKI } from '../utils/webpki';

const CERT_DATE_FIELDS = ['validityEnd', 'validityEndDate', 'notAfter', 'validTo', 'expiresAt'];

const getCertificateEndDate = (cert) => {
  return CERT_DATE_FIELDS.reduce((foundDate, field) => {
    if (foundDate) return foundDate;
    const fieldValue = cert?.[field];
    if (!fieldValue) return null;
    const parsedDate = new Date(fieldValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }, null);
};

const isCertificateSelectable = (cert) => {
  if (!cert?.thumbprint) return false;
  if (cert?.isValid === false || cert?.isExpired === true) return false;
  const endDate = getCertificateEndDate(cert);
  if (endDate && endDate <= new Date()) return false;
  return true;
};

function IcpSignatureConfirmationPage(props) {
  const { signerKey } = useParams();

  const { requestSignatureToken, sendIcpSignature, signaturePosition, setSignaturePosition } = useContext(EntityContext)

  const [certificates, setCertificates] = useState([]);
  const [selectedCert, setSelectedCert] = useState('');
  const [selectedCertThumbprint, setSelectedCertThumbprint] = useState('');
  const [loading, setLoading] = useState(true); // Estado para controlar o carregamento

  
  const navigate = useNavigate();

  const navigateToSignatureFinished = () => {
    // 👇️ navigate to /contacts
    navigate(`/signature/finish`);
  };

  useEffect(() => {
    const pki = createWebPKI();
    pki.init(() => {
      pki.listCertificates().success((certs) => {
        const validCertificates = certs.filter(isCertificateSelectable);
        setCertificates(validCertificates);
        setLoading(false); // Define loading como false após carregar os certificados
      }).error((error) => { // Trate erros
        console.error("Erro ao listar certificados:", error);
        setLoading(false); // Mesmo em caso de erro, pare o loading
        // Exiba uma mensagem de erro para o usuário, se necessário.
      });
    });
  }, []);

  const handleReadCert = async () => {
    if (!selectedCert) return;
  
    try {
      const pki = createWebPKI();
      const certEncoding = await new Promise((resolve, reject) => {
        pki.readCertificate({ thumbprint: selectedCert }).success(resolve).error(reject);
      });
  
      const payload = {
        certificate: certEncoding,
        envelope_signer_key: signerKey,
      };
      if (signaturePosition) {
        payload.signature_position = signaturePosition;
      }

      const data = await requestSignatureToken(payload);
  
      const hashToSign = data.hash;
      const signature = await new Promise((resolve, reject) => {
        pki.signHash({
          thumbprint: selectedCert,
          hash: hashToSign,
          digestAlgorithm: "SHA-256",
        }).success(resolve).error(reject);
      });
  
      const result = await sendIcpSignature(data.token, signerKey, signature);

      if (result) {
        setSignaturePosition?.(null);
        navigateToSignatureFinished();
      }
      
    } catch (error) {
      console.error("Erro:", error);
    }
  };

  const handleSelectCert = (event) => {
    setSelectedCert(event.target.value);
  };

  return (
    <>
      <Helmet>
        <title>Confirmar Assinatura</title>
      </Helmet>

      <Container maxWidth="md"> {/* Largura do container ajustada */}
        <Stack alignItems="center" justifyContent="center" spacing={3} sx={{ mt: 8, mb: 8 }}> {/* Margem superior e inferior ajustada com sx */}
          <Typography variant="h5" gutterBottom>Selecione um Certificado:</Typography> {/* Typography com variant e gutterBottom */}

          {loading ? ( // Exibe o CircularProgress enquanto carrega
            <CircularProgress />
          ) : (
            <>
              <FormControl fullWidth>
                <InputLabel id="cert-select-label">Selecione um certificado</InputLabel>
                <Select
                  labelId="cert-select-label"
                  id="cert-select"
                  value={selectedCert}
                  label="Selecione um certificado"
                  onChange={handleSelectCert}
                >
                  <MenuItem value="">Selecione um certificado</MenuItem>
                  {certificates.map((cert) => (
                    <MenuItem key={cert.thumbprint} value={cert.thumbprint}>
                      {cert.subjectName} (emitido por {cert.issuerName})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!certificates.length && (
                <Alert severity="warning" sx={{ width: '100%' }}>
                  Nenhum certificado valido e nao expirado foi encontrado.
                </Alert>
              )}
            </>
          )}

          <Button variant="contained" onClick={handleReadCert} disabled={!selectedCert} fullWidth> {/* Botão estilizado e desabilitado se nenhum certificado selecionado */}
            Assinar
          </Button>

        </Stack>
      </Container>
    </>
  );
}

export default IcpSignatureConfirmationPage;